import React, { useEffect, useRef } from "react";

/**
 * Dainty blue scratch cursor — smoothed.
 *
 * Two changes vs. a naive trail:
 *   1. Pointer position is lerped toward the raw mouse each frame, so
 *      hardware/browser jitter is filtered before we record samples.
 *   2. The line through samples is rendered with quadratic-bezier curves
 *      (each sample is the control point, midpoints are on-curve anchors),
 *      so the trail flows instead of zig-zagging between vertices.
 */
export default function CursorTrail() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let dpr = Math.max(1, window.devicePixelRatio || 1);
    let points = []; // [{x, y, t}]
    let raf;

    const pointer = {
      x: -9999,
      y: -9999,
      raw: { x: -9999, y: -9999, seeded: false },
    };

    const LIFE_MS = 520;
    const FOLLOW = 0.42; // 0..1 — higher = snappier; lower = smoother
    const MIN_SAMPLE_DIST = 1.2;
    const MAX_POINTS = 240;

    const resize = () => {
      dpr = Math.max(1, window.devicePixelRatio || 1);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + "px";
      canvas.style.height = window.innerHeight + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const onMove = (e) => {
      pointer.raw.x = e.clientX;
      pointer.raw.y = e.clientY;
      pointer.raw.seeded = true;
    };
    window.addEventListener("mousemove", onMove);

    const drawSmoothed = () => {
      // Quadratic-bezier path: each interior point is a control, midpoint
      // between consecutive points is the on-curve anchor. Endpoints are
      // anchored explicitly.
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length - 1; i++) {
        const xc = (points[i].x + points[i + 1].x) / 2;
        const yc = (points[i].y + points[i + 1].y) / 2;
        ctx.quadraticCurveTo(points[i].x, points[i].y, xc, yc);
      }
      ctx.lineTo(
        points[points.length - 1].x,
        points[points.length - 1].y
      );
    };

    const loop = () => {
      const now = performance.now();
      const w = window.innerWidth;
      const h = window.innerHeight;

      // Lerp smoothed pointer toward raw mouse at rAF cadence.
      if (pointer.raw.seeded) {
        if (pointer.x === -9999) {
          pointer.x = pointer.raw.x;
          pointer.y = pointer.raw.y;
        } else {
          pointer.x += (pointer.raw.x - pointer.x) * FOLLOW;
          pointer.y += (pointer.raw.y - pointer.y) * FOLLOW;
        }

        const last = points[points.length - 1];
        if (
          !last ||
          Math.hypot(last.x - pointer.x, last.y - pointer.y) > MIN_SAMPLE_DIST
        ) {
          points.push({ x: pointer.x, y: pointer.y, t: now });
          if (points.length > MAX_POINTS) points.shift();
        }
      }

      while (points.length && now - points[0].t > LIFE_MS) points.shift();

      // Soft erase — gentler decay than the original 0.18 alpha.
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = "rgba(0,0,0,0.13)";
      ctx.fillRect(0, 0, w, h);

      ctx.globalCompositeOperation = "source-over";
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      if (points.length > 2) {
        drawSmoothed();
        ctx.strokeStyle = "hsla(212, 100%, 60%, 0.22)";
        ctx.lineWidth = 1.6;
        ctx.stroke();
        ctx.strokeStyle = "hsla(206, 100%, 78%, 0.95)";
        ctx.lineWidth = 0.7;
        ctx.stroke();
      } else if (points.length === 2) {
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        ctx.lineTo(points[1].x, points[1].y);
        ctx.strokeStyle = "hsla(212, 100%, 60%, 0.22)";
        ctx.lineWidth = 1.6;
        ctx.stroke();
        ctx.strokeStyle = "hsla(206, 100%, 78%, 0.95)";
        ctx.lineWidth = 0.7;
        ctx.stroke();
      }

      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  return <canvas ref={canvasRef} className="cursor-canvas" aria-hidden="true" />;
}
